/* eslint-disable react-hooks/exhaustive-deps */
import { convertTimeZone, has } from '@/commonFunctions/utils'
import { ButtonPrimary, ShareButton } from '@/components/button'
import { CheckCircle, ErrorRoundedIcon, EventsIconRounded } from '@/components/icons'
import { ConfirmModal } from '@/components/modal'
import { EVENT_MODIFY, LIMIT, OFFSET } from '@/constants/Common'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { ROUTE_CLUBS, ROUTE_DASHBOARD, ROUTE_EVENTS } from '@/constants/Routes'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { clubSelector } from '@/pages/club/selectors'
import { userSelector } from '@/pages/user/selectors'
import { setVerifyEmailModalState } from '@/pages/user/userSlice'
import {
  useEditPartner,
  useEndBracket,
  useGetBracketPendingConfirmation,
  useGetBracketTeams,
  useReopenBracket,
  useUpdatePartner,
  useWithdrawPlayer,
} from '@/services'
import { isEmpty, max, min } from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { eventSelector } from '../selectors'
import { AcceptPartnerModal } from './AcceptPartnerModal'
import { EndReopenBracketModal } from './EndReopenBracketModal'
import { EventRegisterAddPartnerModal } from './EventRegisterAddPartnerModal'
import { WithdrawModal } from './WithdrawModal'

type Props = {
  playersLength?: number
  onCallBack: () => void
}

export const BracketHeader = ({ onCallBack }: Props) => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const dispatch = useAppDispatch()
  const { event, bracket } = useAppSelector(eventSelector)
  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)
  const { token } = useAppSelector(authSelector)

  const { eventId, bracketId } = params

  const [checked, setChecked] = useState<{ yes: boolean; no: boolean } | undefined>(undefined)
  const [acceptPartnerModal, setAcceptPartnerModal] = useState(false)
  const [partnerModal, setPartnerModal] = useState(false)
  const [confirmModal, setConfirmModal] = useState(false)
  const [removePartner, setRemovePartner] = useState(false)
  const [withdrawPlayerDialog, setWithdrawPlayerDialog] = useState(false)
  const [isTeamPending, setIsTeamPending] = useState(true)
  const [endReopenBracketModal, setEndReopenBracketModal] = useState<'END' | 'REOPEN' | ''>('')
  const [paramsUpdatePartner, setParamsUpdatePartner] = useState<{
    bracketId: string
    isClubMember: boolean
    registrationId: string
    status: string
  }>({
    bracketId: '',
    isClubMember: false,
    registrationId: '',
    status: '',
  })
  const [paramsEndBracket, setParamsEndBracket] = useState<{
    eventId: string
    bracketId: string
  }>({
    eventId: '',
    bracketId: '',
  })

  const { data: resUpdatePartner, error: errorUpdatePartner } = useUpdatePartner(
    paramsUpdatePartner.bracketId,
    paramsUpdatePartner.isClubMember,
    paramsUpdatePartner.registrationId,
    paramsUpdatePartner.status
  )
  const { data: resEndBracket } = useEndBracket(paramsEndBracket.eventId, paramsEndBracket.bracketId)
  const { mutate: reopenBracket } = useReopenBracket()
  const { mutate: getTeams } = useGetBracketTeams()
  const { mutate: withdrawPlayer } = useWithdrawPlayer()
  const { mutate: editPartner } = useEditPartner()
  const { data: resPendingConfirmation } = useGetBracketPendingConfirmation(Number(bracketId), user?.id)

  const bracketPriceArray = [bracket?.memberFee, bracket?.nonMemberFee]
  const symbol = event?.currencyDetails?.currencySymbol ? event?.currencyDetails?.currencySymbol : '$'
  const bracketString = `${moment(bracket?.duration?.[0])?.format('MMMM DD')} - ${moment(
    bracket?.duration?.[1]
  )?.format('MMMM DD, YYYY')}`
  const shareUrl = window.location.href
  const shareText = `Hi, have a look at this bracket \nBracket Name: ${
    bracket?.name
  } \nBracket Dates: ${bracketString} \nBracket Fee: ${symbol}${min(bracketPriceArray)} - ${symbol}${max(
    bracketPriceArray
  )} \nPlease click the below link to view this bracket`

  const bracketStart = convertTimeZone(bracket?.duration?.[0] as string)
  const bracketEnd = convertTimeZone(bracket?.duration?.[1] as string)

  const activePlayer =
    bracket?.registrationDetails?.player1?.id === user?.id
      ? bracket?.registrationDetails?.player1
      : bracket?.registrationDetails?.player2
  const playerDetails = bracket?.registrationDetails?.isParticipant1
    ? bracket?.registrationDetails?.player1
    : bracket?.registrationDetails?.player2
  const player2 =
    bracket?.registrationDetails?.player1?.id !== user?.id
      ? bracket?.registrationDetails?.player1
      : bracket?.registrationDetails?.player2

  const onCheckVerifyEmail = () => {
    if (!user?.isValidEmail) dispatch(setVerifyEmailModalState(true))
  }

  const toggleAcceptPartnerModal = () => setAcceptPartnerModal(!acceptPartnerModal)

  const togglePartnerModal = () => setPartnerModal(!partnerModal)

  const handleWithdrawPlayer = () => {
    if (bracketId && event && user) {
      withdrawPlayer(
        {
          bracketId: bracketId,
          clubId: event?.clubId.toString(),
          playerId: user?.id,
          registrationId: bracket?.registrationDetails?.registrationId,
        },
        {
          onSuccess: () => {
            setWithdrawPlayerDialog(false)
            navigate(ROUTE_EVENTS)
          },
          onError: (error: any) => {
            setWithdrawPlayerDialog(false)
            toast.error(error?.response?.data?.message || APP_CUSTOM_ERROR)
          },
        }
      )
    }
  }

  const handleRejectPartner = () => {
    if (bracketId)
      setParamsUpdatePartner({
        bracketId: bracketId,
        isClubMember: false,
        registrationId: bracket?.registrationDetails?.registrationId,
        status: 'CANCELLED',
      })
  }

  const onAcceptPartner = () => {
    if (bracketId && checked)
      setParamsUpdatePartner({
        bracketId: bracketId,
        isClubMember: checked?.yes ? true : false,
        registrationId: bracket?.registrationDetails?.registrationId,
        status: 'CONFIRMED',
      })
  }
  const handleRemovePartner = () => {
    if (bracketId)
      editPartner(
        {
          bracketId: Number(bracketId),
          partnerId: null,
          partnerStatus: 'INACTIVE',
          registrationId: bracket?.registrationDetails?.registrationId,
        },
        {
          onSuccess: () => {
            onCallBack()
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || APP_CUSTOM_ERROR)
          },
        }
      )
  }

  const handleSaveAddPartner = (partnerDetails: any) => {
    if (bracketId)
      editPartner(
        {
          bracketId: Number(bracketId),
          partnerId: partnerDetails?.id,
          partnerStatus: 'NOT_CONFIRMED',
          registrationId: bracket?.registrationDetails?.registrationId,
        },
        {
          onSuccess: () => {
            onCallBack()
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || APP_CUSTOM_ERROR)
          },
        }
      )
  }

  const handleEndBracketModal = () => {
    setEndReopenBracketModal('END')
  }

  const handleEndReopenBracket = () => {
    if (eventId && bracketId && endReopenBracketModal === 'END')
      setParamsEndBracket({
        eventId: eventId,
        bracketId: bracketId,
      })

    if (bracket && eventId && bracketId && endReopenBracketModal === 'REOPEN') {
      reopenBracket(
        {
          bracketId: bracketId,
          clubId: bracket?.clubId.toString(),
          leagueId: eventId,
          status: 'ACTIVE',
        },
        {
          onSuccess: () => {
            onCallBack()
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || APP_CUSTOM_ERROR)
          },
        }
      )
    }
  }

  const onClickEditBracket = () => {
    navigate(`${ROUTE_EVENTS}/${event?.leagueId}/bracket/${bracket?.bracketId}/edit`)
  }

  const onClickSeedMatches = () => {
    navigate(`${ROUTE_EVENTS}/${event?.leagueId}/bracket/${bracket?.bracketId}/seed`)
  }

  const onClickViewDraws = () => {
    navigate(`${ROUTE_EVENTS}/${event?.leagueId}/bracket/${bracket?.bracketId}/diagram`)
  }

  useEffect(() => {
    if (resPendingConfirmation?.results.length > 0) {
      setIsTeamPending(true)
    } else {
      setIsTeamPending(false)
    }
  }, [resPendingConfirmation])

  useEffect(() => {
    if (!isTeamPending && token)
      getTeams({
        format: 'DOUBLES',
        bracketId: Number(bracketId),
        limit: LIMIT,
        offset: OFFSET,
      })
  }, [isTeamPending, token])

  useEffect(() => {
    if (resUpdatePartner?.status === 'SUCCESS') {
      if (acceptPartnerModal && !resUpdatePartner.result?.checkoutUrl) {
        navigate(`${ROUTE_EVENTS}/${event?.leagueId}`)
        setAcceptPartnerModal(false)
      }
      if (acceptPartnerModal && resUpdatePartner.result.checkoutUrl) {
        window.location.replace(resUpdatePartner.result?.checkoutUrl)
      }
      onCallBack()
    }

    if (resEndBracket?.status === 'SUCCESS') onCallBack()
  }, [resUpdatePartner, resEndBracket])

  return (
    <>
      <div className="max-w-[720px] flex">
        <EventsIconRounded className="w-10 h-10 text-slate-300" />
        <div className="w-full flex flex-col gap-y-2 ml-3">
          {bracket?.durationDateTime?.length ? (
            <span className="text-white text-xs font-medium text-opacity-60 uppercase">
              {`${moment(bracketStart)?.format('ddd MMM DD')} - ${moment(bracketEnd)?.format('ddd MMM DD')}, ${moment(
                bracketEnd
              )?.format('YY')}`}
            </span>
          ) : null}
          <p className="text-white text-xl md:text-[32px] md:leading-10 font-bold">{bracket?.name}</p>
          <div>
            {token ? (
              <Link
                to={user?.isValidEmail ? `${ROUTE_CLUBS}/${event?.clubId}` : ROUTE_DASHBOARD}
                className="text-white font-semibold text-md underline underline-offset-2"
                onClick={onCheckVerifyEmail}
              >
                {event?.clubName}
              </Link>
            ) : (
              <p className="text-white font-semibold text-md underline underline-offset-2">{event?.clubName}</p>
            )}
          </div>
          <p className="text-white text-opacity-80 text-sm">{bracket?.leagueAddress?.shortAddress}</p>
          {bracket?.registrationDetails ? (
            <>
              {bracket?.format === 'DOUBLES' ? (
                <>
                  {bracket?.registrationDetails?.player1?.partnerStatus === 'CONFIRMED' &&
                  bracket?.registrationDetails?.isParticipant1 ? (
                    <div className="flex items-center gap-x-2 text-white text-sm">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      {'  '}
                      {t('event.partnerConfirmed')}:{'  '}
                      <span className="text-sm font-semibold text-white">{player2?.fullName}</span>
                    </div>
                  ) : (bracket?.registrationDetails?.player1?.partnerStatus === 'NOT_ADDED' ||
                      bracket?.registrationDetails?.player1?.partnerStatus === 'INACTIVE' ||
                      bracket?.registrationDetails?.player1?.partnerStatus === 'CANCELLED') &&
                    isEmpty(player2) ? (
                    <div className="flex items-center gap-x-2 text-white text-sm">
                      <ErrorRoundedIcon className="w-5 h-5 text-boldOrange" />
                      {'  '}
                      {t('event.noPartnerSelected')}
                    </div>
                  ) : bracket?.registrationDetails?.isParticipant1 &&
                    bracket?.registrationDetails?.player1?.partnerStatus === 'NOT_CONFIRMED' ? (
                    <div className="flex items-center gap-x-2 text-white text-sm">
                      <ErrorRoundedIcon className="w-5 h-5 text-boldOrange" />
                      {'  '}
                      {t('event.partnerPending')}{' '}
                      <span className="text-sm font-semibold text-gray-300">{player2?.fullName}</span>
                    </div>
                  ) : !bracket?.registrationDetails?.isParticipant1 && player2?.partnerStatus === 'NOT_CONFIRMED' ? (
                    <div className="flex items-center gap-x-2">
                      <ErrorRoundedIcon className="w-5 h-5 text-boldOrange" />
                      {'  '}
                      <span className="text-white text-sm">
                        {t('event.partnerPending')}: {player2?.fullName}
                      </span>
                      {bracket?.durationStatus === 'COMPLETE' || bracket?.hasConfirmMatch ? null : (
                        <>
                          <ButtonPrimary
                            className="!py-2 !rounded !bg-white !text-navy-900 !text-xs"
                            title={t('common.decline')}
                            onClick={() => setConfirmModal(true)}
                          />
                          <ButtonPrimary
                            className="!py-[6px] !rounded !text-xs"
                            title={t('common.accept')}
                            onClick={() => {
                              toggleAcceptPartnerModal()
                            }}
                          />
                        </>
                      )}
                    </div>
                  ) : !bracket?.registrationDetails?.isParticipant1 && player2?.status === 'CONFIRMED' ? (
                    <div className="flex items-center gap-x-2 text-white text-sm">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      {'  '}
                      {t('event.partnerConfirmed')}:{'  '}
                      <span className="text-sm font-semibold text-gray-300">
                        {bracket?.registrationDetails?.player1?.fullName}
                      </span>
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
          <div className="flex py-1 md:py-2 gap-2 flex-wrap">
            {user && bracket?.elimination === 'COMPASS' && (bracket?.isMatchSeeded || bracket?.hasConfirmMatch) ? (
              <ButtonPrimary title={t('event.viewDraws')} onClick={onClickViewDraws} />
            ) : null}
            {bracket?.displayStatus === 'Completed' ? (
              <ButtonPrimary title={t('common.completed')} disabled />
            ) : has(permissions, EVENT_MODIFY) ? (
              <>
                {!bracket?.hasConfirmMatch ? (
                  <>
                    <ButtonPrimary
                      title={t('event.seedMatches')}
                      className="seed-matches"
                      // disabled={
                      //   bracket?.elimination === 'COMPASS'
                      //     ? bracket.format === 'SINGLES'
                      //       ? (playersLength as number) < 9
                      //       : (resTeams?.result?.hits.length as number) < 9
                      //     : bracket?.format === 'SINGLES'
                      //     ? (playersLength as number) < 2
                      //     : isTeamPending || (resTeams?.result?.hits.length as number) <= 1
                      // }
                      disabled
                      onClick={onClickSeedMatches}
                    >
                      <Tooltip anchorSelect=".seed-matches" place="bottom">
                        <p className="max-w-md">{t('club.seedMatchesDesc')}</p>
                      </Tooltip>
                    </ButtonPrimary>
                    <ButtonPrimary
                      title={t('event.editBracket')}
                      className="hidden lg:flex"
                      onClick={onClickEditBracket}
                    />
                  </>
                ) : (
                  <></>
                )}
                <ButtonPrimary
                  title={t('event.endBracket')}
                  className="hidden lg:flex"
                  onClick={handleEndBracketModal}
                />
                {!isEmpty(bracket?.registrationDetails) &&
                !bracket?.isMatchSeeded &&
                playerDetails?.isRegistered &&
                bracket?.durationStatus !== 'COMPLETE' &&
                playerDetails?.partnerStatus !== 'CONFIRMED' ? (
                  <ButtonPrimary
                    title={t('event.withdraw')}
                    className="hidden lg:flex"
                    onClick={() => setWithdrawPlayerDialog(true)}
                  />
                ) : null}
              </>
            ) : activePlayer?.status === 'CONFIRMED' || activePlayer?.isRegistered ? (
              <>
                <ButtonPrimary title={t('event.registered')} disabled />
                {!isEmpty(bracket?.registrationStatus) &&
                !bracket?.isMatchSeeded &&
                activePlayer?.isRegistered &&
                bracket?.durationStatus !== 'COMPLETE' &&
                activePlayer?.partnerStatus !== 'CONFIRMED' ? (
                  <ButtonPrimary title={t('event.withdraw')} onClick={() => setWithdrawPlayerDialog(true)} />
                ) : null}
              </>
            ) : activePlayer?.status === 'NOT_CONFIRMED' ? (
              <ButtonPrimary title={t('event.invitedPlayer')} disabled={activePlayer?.status === 'NOT_CONFIRMED'} />
            ) : null}
            {bracket?.registrationDetails && bracket?.durationStatus !== 'COMPLETE' && !bracket?.hasConfirmMatch ? (
              <>
                <>
                  {bracket?.format === 'DOUBLES' && !bracket?.hasConfirmMatch ? (
                    <>
                      {activePlayer?.partnerStatus === 'NOT_CONFIRMED' ? (
                        <>
                          <ButtonPrimary title={t('event.editPartner')} onClick={() => setPartnerModal(true)} />
                          <ButtonPrimary title={t('event.removePartner')} onClick={() => setRemovePartner(true)} />
                        </>
                      ) : activePlayer?.partnerStatus === 'CONFIRMED' && !isEmpty(player2) ? null : (
                        <ButtonPrimary title={t('event.addPartner')} onClick={() => setPartnerModal(true)} />
                      )}
                    </>
                  ) : null}
                </>
              </>
            ) : null}
            {bracket?.status === 'COMPLETE' &&
            has(permissions, EVENT_MODIFY, true) &&
            new Date().getTime() < new Date(bracket?.durationDateTime?.[1]).getTime() ? (
              <ButtonPrimary title={t('event.reopenBracket')} onClick={() => setEndReopenBracketModal('REOPEN')} />
            ) : (
              <></>
            )}
            <ShareButton
              shareUrl={shareUrl}
              shareText={shareText}
              pageName={TRACKING_TYPES.BRACKET_HOME}
              shareContent={TRACKING_TYPES.BRACKET_SHARE}
            />
          </div>
        </div>
      </div>
      <WithdrawModal
        title={t('event.withdraw')}
        open={!!withdrawPlayerDialog}
        onClose={() => setWithdrawPlayerDialog(false)}
        leftTitle={t('event.withdraw')}
        rightTitle={t('common.goBack')}
        leftButton={handleWithdrawPlayer}
        content={
          <div className="flex flex-col gap-y-4 items-center justify-center px-8">
            <h1 className="text-2xl text-navy-900 font-bold">{t('event.withdraw')}</h1>
            <p className="text-navy-900 font-medium text-center">{t('event.confirmWithdrawFromBracket')}</p>
            <p className="text-navy-900 font-medium text-center">{t('event.contactTheEventOrganizer')}</p>
          </div>
        }
        rightButton={() => setWithdrawPlayerDialog(false)}
      />
      <AcceptPartnerModal
        open={!!acceptPartnerModal}
        onClose={toggleAcceptPartnerModal}
        onSubmit={() => onAcceptPartner()}
        disabled={!checked?.yes && !checked?.no}
        content={
          <div className="flex flex-col gap-y-4 px-8">
            <span className="text-navy-900">
              {t('event.toRegisterFor')} <span className="font-bold">{bracket?.name}</span> {t('common.with')}{' '}
              <span className="font-bold">{player2?.fullName}</span> {t('event.answerCompleteThePrecess')}
            </span>

            <p className="text-navy-900 font-semibold text-sm">{t('event.clubMembershipQuestion')}</p>

            <div className="flex gap-x-6">
              <label className="flex gap-x-2 items-center">
                <input
                  type="radio"
                  className="w-4 h-4"
                  checked={checked !== undefined ? checked.yes : false}
                  onChange={() =>
                    setChecked({
                      yes: true,
                      no: false,
                    })
                  }
                />
                <span className="text-navy-900 font-semibold text-base">{t('common.yes')}</span>
              </label>
              <label className="flex gap-x-2 items-center">
                <input
                  type="radio"
                  className="w-4 h-4"
                  checked={checked !== undefined ? checked.no : false}
                  onChange={() =>
                    setChecked({
                      yes: false,
                      no: true,
                    })
                  }
                />
                <span className="text-navy-900 font-semibold text-base">{t('common.no')}</span>
              </label>
            </div>
            {(errorUpdatePartner as any)?.response && (
              <p className="text-sm text-boldOrange font-medium">
                {(errorUpdatePartner as any).response.data?.message}
              </p>
            )}
          </div>
        }
      />

      <EventRegisterAddPartnerModal
        open={!!partnerModal}
        onClose={togglePartnerModal}
        currentBracketData={bracket}
        onSubmit={(value) => handleSaveAddPartner(value)}
      />

      <ConfirmModal
        open={confirmModal}
        onClose={() => setConfirmModal(false)}
        title={t('event.declineInvite')}
        desc={`${t('event.declineInviteFirstDesc')} ${player2?.fullName} ${t('event.declineInviteSecondDesc')}`}
        onSubmit={handleRejectPartner}
      />

      <ConfirmModal
        open={removePartner}
        onClose={() => setRemovePartner(false)}
        title={t('event.removePartner')}
        desc={`${t('event.removePartnerFirstDesc')} ${player2?.fullName} ${t('event.removePartnerSendDesc')}`}
        onSubmit={handleRemovePartner}
      />

      <EndReopenBracketModal
        open={endReopenBracketModal}
        onClose={() => setEndReopenBracketModal('')}
        onSubmit={handleEndReopenBracket}
      />
    </>
  )
}
